/**
 * Adjustable Frame Component
 * 
 * A user-adjustable frame overlay for manual book page framing.
 * Features:
 * - 4 draggable corner handles (44px touch targets for accessibility)
 * - SVG mask that darkens area outside the frame
 * - Preset shapes: Portrait, Landscape, Square, Spread
 * - Frame persists between captures (same book = same shape)
 * 
 * The frame uses normalized coordinates (0-1) for responsive positioning.
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Square, RectangleHorizontal, RectangleVertical, Book } from 'lucide-react';

// =============================================================================
// TYPES
// =============================================================================

export interface FrameCorners {
  topLeft: { x: number; y: number };
  topRight: { x: number; y: number };
  bottomLeft: { x: number; y: number };
  bottomRight: { x: number; y: number };
}

export type FramePreset = 'portrait' | 'landscape' | 'square' | 'spread' | 'custom';

export interface AdjustableFrameProps {
  /** Current frame corners (normalized 0-1) */
  corners: FrameCorners;
  /** Callback when corners change */
  onCornersChange: (corners: FrameCorners) => void;
  /** Current preset */
  preset?: FramePreset;
  /** Callback when preset changes */
  onPresetChange?: (preset: FramePreset) => void;
  /** Container dimensions for coordinate conversion */
  containerWidth: number;
  containerHeight: number;
  /** Whether to show preset buttons */
  showPresets?: boolean;
  /** Whether frame is locked (non-draggable) */
  locked?: boolean;
  /** Frame color (default: white) */
  frameColor?: string;
  /** Mask opacity (default: 0.5) */
  maskOpacity?: number;
}

// =============================================================================
// CONSTANTS
// =============================================================================

const HANDLE_SIZE = 44; // Touch target size in pixels
const CORNER_ACCENT_SIZE = 40;
const MIN_FRAME_SIZE = 0.1; // Minimum frame dimension (10% of container)

/** Preset frame shapes */
const FRAME_PRESETS: Record<FramePreset, FrameCorners | null> = {
  portrait: {
    topLeft: { x: 0.15, y: 0.05 },
    topRight: { x: 0.85, y: 0.05 },
    bottomLeft: { x: 0.15, y: 0.95 },
    bottomRight: { x: 0.85, y: 0.95 },
  },
  landscape: {
    topLeft: { x: 0.05, y: 0.15 },
    topRight: { x: 0.95, y: 0.15 },
    bottomLeft: { x: 0.05, y: 0.85 },
    bottomRight: { x: 0.95, y: 0.85 },
  },
  square: {
    topLeft: { x: 0.15, y: 0.15 },
    topRight: { x: 0.85, y: 0.15 },
    bottomLeft: { x: 0.15, y: 0.85 },
    bottomRight: { x: 0.85, y: 0.85 },
  },
  spread: {
    topLeft: { x: 0.02, y: 0.1 },
    topRight: { x: 0.98, y: 0.1 },
    bottomLeft: { x: 0.02, y: 0.9 },
    bottomRight: { x: 0.98, y: 0.9 },
  },
  custom: null,
};

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

/** Convert normalized coordinates to pixels */
function toPixels(
  normalized: { x: number; y: number },
  width: number,
  height: number
): { x: number; y: number } {
  return {
    x: normalized.x * width,
    y: normalized.y * height,
  };
}

/** Convert pixel coordinates to normalized */
function toNormalized(
  pixels: { x: number; y: number },
  width: number,
  height: number
): { x: number; y: number } {
  return {
    x: Math.max(0, Math.min(1, pixels.x / width)),
    y: Math.max(0, Math.min(1, pixels.y / height)),
  };
}

/** Generate SVG path for frame polygon */
function getFramePath(corners: FrameCorners, width: number, height: number): string {
  const tl = toPixels(corners.topLeft, width, height);
  const tr = toPixels(corners.topRight, width, height);
  const br = toPixels(corners.bottomRight, width, height);
  const bl = toPixels(corners.bottomLeft, width, height);
  
  return `M ${tl.x} ${tl.y} L ${tr.x} ${tr.y} L ${br.x} ${br.y} L ${bl.x} ${bl.y} Z`;
}

/** Ensure minimum frame size constraints */
function constrainCorners(
  corners: FrameCorners,
  draggedCorner: keyof FrameCorners,
  newPos: { x: number; y: number }
): FrameCorners {
  const result = { ...corners, [draggedCorner]: newPos };
  
  // Ensure minimum width
  const leftX = Math.min(result.topLeft.x, result.bottomLeft.x);
  const rightX = Math.max(result.topRight.x, result.bottomRight.x);
  if (rightX - leftX < MIN_FRAME_SIZE) {
    // Revert the x change
    result[draggedCorner] = { ...result[draggedCorner], x: corners[draggedCorner].x };
  }
  
  // Ensure minimum height
  const topY = Math.min(result.topLeft.y, result.topRight.y);
  const bottomY = Math.max(result.bottomLeft.y, result.bottomRight.y);
  if (bottomY - topY < MIN_FRAME_SIZE) {
    // Revert the y change
    result[draggedCorner] = { ...result[draggedCorner], y: corners[draggedCorner].y };
  }
  
  return result;
}

// =============================================================================
// CORNER HANDLE COMPONENT
// =============================================================================

interface CornerHandleProps {
  position: { x: number; y: number };
  corner: keyof FrameCorners;
  containerWidth: number;
  containerHeight: number;
  onDragStart: (corner: keyof FrameCorners, e: React.TouchEvent | React.MouseEvent) => void;
  locked: boolean;
  frameColor: string;
}

const CornerHandle: React.FC<CornerHandleProps> = ({
  position,
  corner,
  containerWidth,
  containerHeight,
  onDragStart,
  locked,
  frameColor,
}) => {
  const pixelPos = toPixels(position, containerWidth, containerHeight);
  
  // Determine corner accent direction
  const isLeft = corner.includes('Left');
  const isTop = corner.includes('top');
  
  return (
    <div
      className={`absolute touch-none ${locked ? 'cursor-default' : 'cursor-move'}`}
      style={{
        left: pixelPos.x - HANDLE_SIZE / 2,
        top: pixelPos.y - HANDLE_SIZE / 2,
        width: HANDLE_SIZE,
        height: HANDLE_SIZE,
      }}
      onTouchStart={(e) => !locked && onDragStart(corner, e)}
      onMouseDown={(e) => !locked && onDragStart(corner, e)}
    >
      {/* Touch target (invisible) */}
      <div className="absolute inset-0" />
      
      {/* Visual corner bracket */}
      <div
        className="absolute"
        style={{
          left: isLeft ? 0 : HANDLE_SIZE - CORNER_ACCENT_SIZE,
          top: isTop ? 0 : HANDLE_SIZE - CORNER_ACCENT_SIZE,
          width: CORNER_ACCENT_SIZE,
          height: CORNER_ACCENT_SIZE,
          borderColor: frameColor,
          borderWidth: 4,
          borderStyle: 'solid',
          borderTopWidth: isTop ? 4 : 0,
          borderBottomWidth: isTop ? 0 : 4,
          borderLeftWidth: isLeft ? 4 : 0,
          borderRightWidth: isLeft ? 0 : 4,
          borderRadius: isTop && isLeft ? '8px 0 0 0' :
                        isTop && !isLeft ? '0 8px 0 0' :
                        !isTop && isLeft ? '0 0 0 8px' :
                        '0 0 8px 0',
        }}
      />
      
      {/* Center dot for visibility */}
      <div
        className="absolute w-3 h-3 rounded-full"
        style={{
          left: '50%',
          top: '50%',
          transform: 'translate(-50%, -50%)',
          backgroundColor: frameColor,
          boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
        }}
      />
    </div>
  );
};

// =============================================================================
// PRESET BUTTON COMPONENT
// =============================================================================

interface PresetButtonProps {
  preset: FramePreset;
  label: string;
  icon: React.ReactNode;
  isActive: boolean;
  onClick: () => void;
}

const PresetButton: React.FC<PresetButtonProps> = ({
  label,
  icon,
  isActive,
  onClick,
}) => (
  <button
    onClick={onClick}
    className={`
      flex flex-col items-center justify-center p-2 rounded-lg transition-all
      ${isActive 
        ? 'bg-white text-gray-900 shadow-lg' 
        : 'bg-black/30 text-white/80 hover:bg-black/40'
      }
    `}
    style={{ minWidth: 56 }}
  >
    {icon}
    <span className="text-xs mt-1">{label}</span>
  </button>
);

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export const AdjustableFrame: React.FC<AdjustableFrameProps> = ({
  corners,
  onCornersChange,
  preset = 'custom',
  onPresetChange,
  containerWidth,
  containerHeight,
  showPresets = true,
  locked = false,
  frameColor = '#ffffff',
  maskOpacity = 0.5,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [draggingCorner, setDraggingCorner] = useState<keyof FrameCorners | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  
  // Track actual rendered container dimensions for HTML positioning
  // (SVG viewBox uses containerWidth/Height and scales automatically,
  // but HTML div handles need actual pixel dimensions)
  const [renderedSize, setRenderedSize] = useState({ width: 0, height: 0 });
  
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    
    // Initial measurement
    const rect = container.getBoundingClientRect();
    setRenderedSize({ width: rect.width, height: rect.height });
    
    // Watch for resize (orientation change, window resize)
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) {
        setRenderedSize({
          width: entry.contentRect.width,
          height: entry.contentRect.height,
        });
      }
    });
    
    observer.observe(container);
    return () => observer.disconnect();
  }, []);
  
  // Handle drag start
  const handleDragStart = useCallback((
    corner: keyof FrameCorners,
    e: React.TouchEvent | React.MouseEvent
  ) => {
    if (locked) return;
    
    e.preventDefault();
    setDraggingCorner(corner);
    setIsDragging(true);
    
    // Mark preset as custom when user starts dragging
    if (onPresetChange && preset !== 'custom') {
      onPresetChange('custom');
    }
  }, [locked, preset, onPresetChange]);
  
  // Handle drag move
  // Uses actual rendered dimensions, not video source dimensions
  const handleMove = useCallback((clientX: number, clientY: number) => {
    if (!draggingCorner || !containerRef.current) return;
    if (renderedSize.width === 0 || renderedSize.height === 0) return;
    
    const rect = containerRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const y = clientY - rect.top;
    
    // Use actual rendered size for coordinate conversion
    const normalized = toNormalized(
      { x, y },
      renderedSize.width,
      renderedSize.height
    );
    
    const newCorners = constrainCorners(corners, draggingCorner, normalized);
    onCornersChange(newCorners);
  }, [draggingCorner, corners, renderedSize.width, renderedSize.height, onCornersChange]);
  
  // Handle drag end
  const handleDragEnd = useCallback(() => {
    setDraggingCorner(null);
    setIsDragging(false);
  }, []);
  
  // Touch/mouse event handlers
  useEffect(() => {
    if (!isDragging) return;
    
    const handleTouchMove = (e: TouchEvent) => {
      e.preventDefault();
      const touch = e.touches[0];
      handleMove(touch.clientX, touch.clientY);
    };
    
    const handleMouseMove = (e: MouseEvent) => {
      handleMove(e.clientX, e.clientY);
    };
    
    const handleEnd = () => handleDragEnd();
    
    window.addEventListener('touchmove', handleTouchMove, { passive: false });
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('touchend', handleEnd);
    window.addEventListener('mouseup', handleEnd);
    
    return () => {
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('touchend', handleEnd);
      window.removeEventListener('mouseup', handleEnd);
    };
  }, [isDragging, handleMove, handleDragEnd]);
  
  // Handle preset selection
  const handlePresetSelect = useCallback((newPreset: FramePreset) => {
    const presetCorners = FRAME_PRESETS[newPreset];
    if (presetCorners) {
      onCornersChange(presetCorners);
    }
    if (onPresetChange) {
      onPresetChange(newPreset);
    }
  }, [onCornersChange, onPresetChange]);
  
  // Generate frame path for SVG mask
  const framePath = getFramePath(corners, containerWidth, containerHeight);
  
  return (
    <div 
      ref={containerRef}
      className="absolute inset-0 pointer-events-none z-[90]"
      style={{ touchAction: 'none' }}
    >
      {/* SVG Mask - darkens area outside frame */}
      <svg 
        className="absolute inset-0 w-full h-full"
        viewBox={`0 0 ${containerWidth} ${containerHeight}`}
        preserveAspectRatio="none"
      >
        <defs>
          <mask id="frame-mask">
            <rect width="100%" height="100%" fill="white" />
            <path d={framePath} fill="black" />
          </mask>
        </defs>
        
        {/* Darkened overlay */}
        <rect 
          width="100%" 
          height="100%" 
          fill="black" 
          opacity={maskOpacity}
          mask="url(#frame-mask)"
        />
        
        {/* Frame border */}
        <path 
          d={framePath} 
          fill="none" 
          stroke={frameColor}
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      
      {/* Corner handles - use actual rendered dimensions */}
      <div className="pointer-events-auto">
        {(['topLeft', 'topRight', 'bottomLeft', 'bottomRight'] as const).map(corner => (
          <CornerHandle
            key={corner}
            position={corners[corner]}
            corner={corner}
            containerWidth={renderedSize.width || 1}
            containerHeight={renderedSize.height || 1}
            onDragStart={handleDragStart}
            locked={locked}
            frameColor={frameColor}
          />
        ))}
      </div>
      
      {/* Preset buttons - positioned above manual capture area (bottom ~160px) */}
      {showPresets && (
        <div 
          className="absolute bottom-40 left-1/2 -translate-x-1/2 flex gap-2 p-2 rounded-xl pointer-events-auto"
          style={{ 
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            backdropFilter: 'blur(8px)',
          }}
        >
          <PresetButton
            preset="portrait"
            label="Portrait"
            icon={<RectangleVertical className="w-5 h-5" />}
            isActive={preset === 'portrait'}
            onClick={() => handlePresetSelect('portrait')}
          />
          <PresetButton
            preset="landscape"
            label="Landscape"
            icon={<RectangleHorizontal className="w-5 h-5" />}
            isActive={preset === 'landscape'}
            onClick={() => handlePresetSelect('landscape')}
          />
          <PresetButton
            preset="square"
            label="Square"
            icon={<Square className="w-5 h-5" />}
            isActive={preset === 'square'}
            onClick={() => handlePresetSelect('square')}
          />
          <PresetButton
            preset="spread"
            label="Spread"
            icon={<Book className="w-5 h-5" />}
            isActive={preset === 'spread'}
            onClick={() => handlePresetSelect('spread')}
          />
        </div>
      )}
    </div>
  );
};

// =============================================================================
// EXPORTS
// =============================================================================

export { FRAME_PRESETS };
// FrameCorners and FramePreset already exported at definition
export default AdjustableFrame;
